/** @jsx jsx */
import { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { jsx } from '@emotion/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { orderBy } from 'lodash';
import {
  Typography,
  useMediaQuery,
  Grid,
  TablePagination,
} from '@material-ui/core';

import ChallengeRankCard from 'components/pages/DetailsPage/common/ChallengeRankCard';
import useBoolean from 'core/utils/use-boolean.hook';
import { challengeResults } from 'actions/challenge.actions';
import {
  ChallengeResultType,
  ICustomTheme,
  IChallengesPartial,
  ResultPerProductType,
} from 'interfaces';
import {
  ChallengeSection,
  ChallengeSectionSkeleton,
  SortToggle,
  TablePaginationActions,
} from 'components';
import { userInfosSelector } from 'reducers/app.reducer';
import {
  challengeListUsersResult,
  isChallengeListResultPerProduct,
  challengeResultsSelector,
} from 'reducers/challenge.reducer';
import { ChallengeListDetailsContext } from '..';
import { GOAL } from 'constants/challenges';
import { LOADING_STATUS } from 'constants/status';
import * as S from './styles';

type resultType = ResultPerProductType | ChallengeResultType;

function ChallengeRankingList({
  challenge,
  productCurrency,
  results,
  hasUserResult,
}: {
  challenge: IChallengesPartial;
  productCurrency: string;
  results: resultType[];
  hasUserResult: boolean;
}) {
  const [page, setPage] = useState(0);
  const { value: isAsc, toggle: toggleIsAsc } = useBoolean(true);
  const { t } = useTranslation();
  const isDownSm = useMediaQuery(
    (theme: ICustomTheme) => theme.breakpoints.down('sm'),
    { noSsr: true },
  );
  const { id: currentUserId } = useSelector(userInfosSelector);

  const rowsPerPage = 9;
  const isPaginationEnabled = !isDownSm && results.length > rowsPerPage;

  const stringResults = JSON.stringify(results);
  const sortedResults = useMemo(
    () => orderBy(results, 'rank', isAsc ? 'asc' : 'desc'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAsc, stringResults],
  );

  const displayedResults = useMemo(
    () =>
      isPaginationEnabled
        ? sortedResults.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage,
        )
        : sortedResults,
    [page, isPaginationEnabled, sortedResults],
  );

  type getRankCardPropsResultType = {
    areResultsAnonymized: boolean;
    mode: 'value' | 'percent';
    unit?: string | JSX.Element;
    goalValue?: number;
  } & Pick<resultType, 'entity' | 'rank' | 'resultValue'>;
  const getRankCardProps = (result: resultType): getRankCardPropsResultType => {
    const commonProps: getRankCardPropsResultType = {
      areResultsAnonymized: challenge.areResultsAnonymized,
      mode: 'value',
      entity: result.entity,
      rank: result.rank,
      resultValue: result.resultValue,
    };

    switch (challenge.goalType) {
      case GOAL.RESULT:
        return {
          ...commonProps,
          unit: challenge.isRewardingGlobal
            ? challenge.resultUnit
            : productCurrency,
        };
      case GOAL.GENERIC: {
        const maxGoalValue =
          challenge.challengeGoals?.[challenge.challengeGoals?.length - 1]
            ?.value;
        const goalUnitRef = challenge.challengeGoals?.[0]?.unit;
        return {
          ...commonProps,
          goalValue: maxGoalValue,
          unit: goalUnitRef,
        };
      }
      case GOAL.BY_PROFILE:
      case GOAL.CUSTOM:
        return {
          ...commonProps,
          goalValue: (result as ChallengeResultType).customResultValue,
          mode: 'percent',
          unit: (
            <S.GoalUnit>{t('challenges.detail.rank.of_the_goal')}</S.GoalUnit>
          ),
        };
      default:
        // eslint-disable-next-line no-console
        console.warn(
          `Wrong goal type in ChallengeRankList: ${challenge.goalType}.
          Should be one of ${GOAL.RESULT}, ${GOAL.GENERIC}, ${GOAL.CUSTOM} or ${GOAL.BY_PROFILE}.`,
        );
        return commonProps;
    }
  };

  // scroll behavior management:
  const [shouldScrollToRankCard, setShouldScrollToRankCard] = useState(false);
  const userRankCardRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (shouldScrollToRankCard) {
      goToUserRankCard();
      setShouldScrollToRankCard(false);
    }
  }, [shouldScrollToRankCard]);

  const goToUserRankCard = () => {
    userRankCardRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const goToCurrentUserPage = () => {
    const currentUserResultIndex = sortedResults.findIndex((result) =>
      '@id' in result.entity
        ? result.entity['@id'].split('/v2/users/')[1] === currentUserId
        : result.entity.userId === currentUserId,
    );
    const currentUserPage = Math.trunc(currentUserResultIndex / rowsPerPage);
    setPage(currentUserPage);
    setShouldScrollToRankCard(true);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
    setShouldScrollToRankCard(true);
  };

  const isJustOneRankCard = displayedResults.length === 1;
  const isFirstPage = page === 0;
  const positionButtonDisplayed = !(isJustOneRankCard && isFirstPage);
  const onSeeMyPositionButtonClick = isDownSm
    ? goToUserRankCard
    : goToCurrentUserPage;

  return (
    <S.Container>
      <S.ToolBar>
        <div>
          <SortToggle
            isAsc={isAsc}
            toggleIsAsc={toggleIsAsc}
            ascLabel={t('challenges.detail.generalRanking.ascendingRanks')}
            descLabel={t('challenges.detail.generalRanking.descendingRanks')}
          />
        </div>
        {positionButtonDisplayed && (
          <S.SeeMyPositionContainer
            onClick={hasUserResult ? onSeeMyPositionButtonClick : undefined}
            role="button"
            isDisabled={!hasUserResult}
          >
            <ArrowDownwardIcon />
            <span>{t('challenges.detail.rank.see_me')}</span>
            <ArrowDownwardIcon />
          </S.SeeMyPositionContainer>
        )}
      </S.ToolBar>

      <Grid container spacing={6} justify="center">
        {displayedResults.map((result) => (
          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            key={
              '@id' in result.entity
                ? result.entity['@id']
                : result.entity.userId
            }
          >
            <ChallengeRankCard
              {...getRankCardProps(result)}
              ref={userRankCardRef}
            />
          </Grid>
        ))}
        {isPaginationEnabled && (
          <Grid item xs={12} alignItems="flex-end">
            <TablePagination
              component="div"
              rowsPerPageOptions={[]}
              page={page}
              count={results.length}
              rowsPerPage={rowsPerPage!}
              onPageChange={handleChangePage}
              ActionsComponent={TablePaginationActions}
            />
          </Grid>
        )}
      </Grid>
    </S.Container>
  );
}

export default function GeneralRankingSection({
  isChallengeEnded,
}: {
  isChallengeEnded: boolean;
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { challenge, challengeResult, productCurrency } = useContext(
    ChallengeListDetailsContext,
  );
  const isResultPerProduct = useSelector(
    isChallengeListResultPerProduct(challenge.id),
  );
  const { status: resultsStatus } = useSelector(
    challengeResultsSelector(challenge.id),
  );
  const results = useSelector(challengeListUsersResult(challenge.id));
  const hasResult = !!results.length;

  const isGoalMecanicByProduct = !challenge?.isRewardingGlobal;
  useEffect(() => {
    if (!isGoalMecanicByProduct) {
      dispatch(
        challengeResults.request({
          challengeId: challenge.id,
          all: true,
        }),
      );
    }
  }, [
    dispatch,
    isResultPerProduct,
    challenge.id,
    challenge.isRewardingAuto,
    isGoalMecanicByProduct,
  ]);

  if (resultsStatus === LOADING_STATUS.PENDING) {
    return <ChallengeSectionSkeleton />;
  }

  return (
    <ChallengeSection
      defaultExpanded={hasResult}
      title={<strong>{t('challenges.detail.generalRanking.title')}</strong>}
    >
      {hasResult ? (
        <ChallengeRankingList
          challenge={challenge}
          productCurrency={productCurrency!}
          results={results}
          hasUserResult={!!challengeResult}
        />
      ) : (
        <Typography>
          {t(
            isChallengeEnded
              ? 'challenges.detail.generalRanking.no_result'
              : 'challenges.detail.generalRanking.no_result_yet',
          )}
        </Typography>
      )}
    </ChallengeSection>
  );
}
